import { useState } from 'react';
import { formatCents } from '@/lib/money';
import { useQuery } from '@tanstack/react-query';

/**
 * The true cost of one job. Task T077, FR-210, FR-502.
 *
 * Fetched on demand rather than with the board, because it is one request per
 * job and most cards are never opened. It is also the request a staff account
 * is refused, so asking for every card would mean a wall of 403s in the console
 * for the majority of the people using this screen.
 *
 * The panel does not decide who may see it. It asks, and reports what came
 * back: a 403 says so in words rather than rendering an empty box, because an
 * empty box reads as "this job cost nothing".
 */
interface JobCost {
  job_id: string;
  units_ordered: number;
  good_cost_cents: number;
  waste_cost_cents: number;
  total_cost_cents: number;
  cost_per_good_unit_cents: number | null;
}



export function JobCostPanel({ jobId }: { jobId: string }) {
  const [open, setOpen] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ['admin', 'job-cost', jobId],
    enabled: open,
    retry: false,
    queryFn: async (): Promise<JobCost> => {
      const res = await fetch(`/api/jobs/${jobId}/cost`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Cost request failed (${res.status})`);
      }
      return (await res.json()) as JobCost;
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        draggable={false}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onDragStart={(e) => e.preventDefault()}
        className="mt-2 text-[12px] text-muted underline decoration-dotted"
      >
        Show cost
      </button>
    );
  }

  return (
    <div
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      className="mt-2 rounded-[8px] border border-line p-2 text-[12px]"
    >
      {isLoading && <p className="text-muted">Working it out…</p>}

      {error && (
        // Say which door is shut. "Nothing here" is what a bug looks like too.
        <p className="text-muted">{(error as Error).message}</p>
      )}

      {data && (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
          <dt className="text-muted">Materials</dt>
          <dd className="text-right">{formatCents(data.good_cost_cents)}</dd>
          <dt className="text-muted">Waste</dt>
          <dd className="text-right">{formatCents(data.waste_cost_cents)}</dd>
          <dt className="font-semibold">Total</dt>
          <dd className="text-right font-semibold">{formatCents(data.total_cost_cents)}</dd>
          {data.cost_per_good_unit_cents != null && (
            <>
              <dt className="text-muted">Per unit</dt>
              <dd className="text-right">{formatCents(data.cost_per_good_unit_cents)}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}
